import { addPointModeAtom } from '@/atoms';
import { selectedFeatureAtom } from '@/atoms/selection.atom';
import { CrosshairMarker } from '@/components/3D/CrosshairMarker';
import { project } from '@/core';
import { execute } from '@/graphql/execute';
import { useFeatureActions } from '@/hooks/useFeatureActions';
import { createFeatureMutation } from '@/queries/feature.query';
import { PointCloudOctree } from '@pnext/three-loader';
import { useThree, type ThreeEvent } from '@react-three/fiber';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import * as THREE from 'three';

interface AddPointToolProps {
  targets: PointCloudOctree[];
  origin: THREE.Vector3;
  enabled: boolean;
  targetLayerId: string;
  srid: number;
}

const DEFAULT_PROPERTIES = { Comment: '' };

export const AddPointTool = ({ targets, srid, enabled, targetLayerId }: AddPointToolProps) => {
  const [draftPoint, setDraftPoint] = useState<THREE.Vector3 | null>(null);
  const [hoverPoint, setHoverPoint] = useState<THREE.Vector3 | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const camera = useThree((state) => state.camera);
  const renderer = useThree((state) => state.gl);
  const selectedFeature = useAtomValue(selectedFeatureAtom);
  const setSelectedFeature = useSetAtom(selectedFeatureAtom);
  const queryClient = useQueryClient();
  const { selectFeatureForEditing, createUpdateHandler } = useFeatureActions();
  const [_, setAddPointMode] = useAtom(addPointModeAtom);
  const { t } = useTranslation('components');

  // refs for dragging state
  const draggingRef = useRef<{
    plane: THREE.Plane;
    offset: THREE.Vector3;
  } | null>(null);

  // Track if we just finished dragging to prevent immediate click
  const justDraggedRef = useRef(false);

  // Track previous selectedFeature to detect when it's nulled from external source
  const prevSelectedFeatureRef = useRef<any>(null);

  // Handler for saving draft feature
  const handleSaveDraftFeature = async (feature: any) => {
    const toastId = toast.loading(t('addPointTool.creatingFeature'));
    try {
      const projectedCoords = await project(feature.geometry.coordinates, `EPSG:${srid.toString()}`);

      const input: any = {
        layerId: feature.layerId,
        geometry: feature.geometry,
        properties: feature.properties || DEFAULT_PROPERTIES,
      };

      if (projectedCoords) {
        input.geometryWGS84 = {
          type: 'Point',
          crs: {
            type: 'name',
            properties: {
              name: 'urn:ogc:def:crs:EPSG::4326',
            },
          },
          coordinates: projectedCoords,
        };
      }

      const result = await execute(createFeatureMutation, { input });

      if (result?.createFeature) {
        const newFeature = result.createFeature;
        toast.success(t('addPointTool.featureCreated'), { id: toastId });
        // Invalidate feature queries to refresh layer data
        queryClient.invalidateQueries({ queryKey: ['features', feature.layerId] });

        // Reset draft state
        setAddPointMode({ enabled: false, targetLayerId: null, draftFeature: null });

        // Select the newly created feature for editing
        selectFeatureForEditing({
          ...newFeature,
          layerId: feature.layerId,
          onSave: createUpdateHandler(newFeature.id, feature.layerId),
        });
      } else {
        throw new Error(t('addPointTool.failedCreateFeature'));
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.message || t('addPointTool.failedCreateFeature');
      toast.error(errorMessage, { id: toastId });
    }
  };

  // Reset cursor when mode is disabled
  useEffect(() => {
    if (!enabled) {
      renderer.domElement.style.cursor = 'default';
      setHoverPoint(null);
    }
  }, [enabled, renderer]);

  // Reset AddPointTool state when selectedFeature is nulled from external source
  useEffect(() => {
    // If selectedFeature was set and is now null, reset draft point
    if (prevSelectedFeatureRef.current && selectedFeature?.isDraft !== true) {
      setDraftPoint(null);
      setIsDragging(false);
      draggingRef.current = null;
      justDraggedRef.current = false;
    }
    prevSelectedFeatureRef.current = selectedFeature;
  }, [selectedFeature]);

  // Hover handler - show preview marker and change cursor
  useEffect(() => {
    if (!enabled || draftPoint) return; // Don't show hover preview if draft point exists

    const handleMouseMove = (e: MouseEvent) => {
      // Calculate normalized device coordinates
      const rect = renderer.domElement.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      // Configure raycaster
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hits = targets.map((t) => t.pick(renderer, camera, raycaster.ray)).filter(Boolean);

      if (hits.length > 0) {
        setHoverPoint(hits[0]!.position!.clone());
        renderer.domElement.style.cursor = 'crosshair';
      } else {
        setHoverPoint(null);
        renderer.domElement.style.cursor = 'default';
      }
    };

    renderer.domElement.addEventListener('pointermove', handleMouseMove);
    return () => {
      renderer.domElement.removeEventListener('pointermove', handleMouseMove);
      renderer.domElement.style.cursor = 'default';
    };
  }, [enabled, draftPoint, targets, camera, renderer, raycaster]);

  // Click handler - raycasting to create draft point
  useEffect(() => {
    if (!enabled) return;

    const handleClick = (e: MouseEvent) => {
      // Don't create point if we just finished dragging
      if (justDraggedRef.current) {
        justDraggedRef.current = false;
        return;
      }

      // Don't create point if already dragging or if draft point already exists
      if (draggingRef.current || isDragging || draftPoint) return;

      // Calculate normalized device coordinates
      const rect = renderer.domElement.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hits = targets.map((t) => t.pick(renderer, camera, raycaster.ray)).filter(Boolean);

      if (hits.length === 0 || !hits[0]?.position) return;

      const hoverPoint = hits[0].position.clone();
      setDraftPoint(hoverPoint);
      setHoverPoint(null); // Clear hover point when draft is created

      // Set selectedFeatureAtom with draft feature
      setSelectedFeature({
        id: '', // No id for draft features
        layerId: targetLayerId,
        geometry: {
          type: 'Point',
          crs: {
            type: 'name',
            properties: {
              name: `urn:ogc:def:crs:EPSG::${srid}`,
            },
          },
          coordinates: [hoverPoint.x, hoverPoint.y, hoverPoint.z],
        },
        properties: DEFAULT_PROPERTIES,
        isDraft: true,
        isMoving: true,
        onSave: handleSaveDraftFeature,
      } as any);
    };

    renderer.domElement.addEventListener('pointerup', handleClick);
    return () => renderer.domElement.removeEventListener('pointerup', handleClick);
  }, [enabled, targets, camera, renderer, raycaster, isDragging, draftPoint]);

  // Drag handler for repositioning draft point
  const startDrag = (e: ThreeEvent<PointerEvent>) => {
    if (!draftPoint) return;

    e.stopPropagation();
    setIsDragging(true);

    const point = e.point.clone();
    const camDir = new THREE.Vector3().copy(camera.getWorldDirection(new THREE.Vector3()));
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(camDir, point);
    const offset = point.clone().sub(draftPoint);
    draggingRef.current = { plane, offset };

    const dom = renderer.domElement;

    const onPointerMove = (ev: PointerEvent) => {
      ev.stopPropagation();
      const rect = dom.getBoundingClientRect();
      const x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const intersectPoint = new THREE.Vector3();
      if (raycaster.ray.intersectPlane(plane, intersectPoint)) {
        const newPosition = intersectPoint.clone().sub(offset);
        setDraftPoint(newPosition);

        // Update coordinates in real-time during drag
        setSelectedFeature((prev) => {
          if (!prev || !(prev as any).isDraft) return prev;
          return {
            ...prev,
            geometry: {
              ...prev.geometry,
              coordinates: [newPosition.x, newPosition.y, newPosition.z],
            },
          } as any;
        });
      }
    };

    const onPointerUp = () => {
      window.removeEventListener('pointermove', onPointerMove, true);
      window.removeEventListener('pointerup', onPointerUp);

      const hits = targets.map((t) => t.pick(renderer, camera, raycaster.ray)).filter(Boolean);

      if (hits.length > 0) {
        const hit = hits[0];
        setDraftPoint(hit!.position!.clone());

        // Update draft feature coordinates in selectedFeatureAtom
        setSelectedFeature((prev) => {
          if (!prev || !(prev as any).isDraft) return prev;
          return {
            ...prev,
            geometry: {
              ...prev.geometry,
              coordinates: [hit!.position!.x, hit!.position!.y, hit!.position!.z],
            },
          } as any;
        });
      }

      draggingRef.current = null;
      setIsDragging(false);

      // Set flag to prevent click handler from firing immediately after drag
      justDraggedRef.current = true;
    };

    window.addEventListener('pointermove', onPointerMove, true);
    window.addEventListener('pointerup', onPointerUp);
  };

  if (!enabled) return null;

  return (
    <>
      {/* Hover preview marker — same shape as the draft, lower opacity. */}
      {hoverPoint && !draftPoint && (
        <CrosshairMarker
          position={hoverPoint}
          color='#ff6b00'
          opacity={0.5}
          sphereOpacity={0.4}
        />
      )}

      {/* Draft point marker — draggable. */}
      {draftPoint && (
        <CrosshairMarker
          position={draftPoint}
          color='#ff6f88'
          opacity={0.9}
          sphereOpacity={0.5}
          onPointerDown={startDrag}
        />
      )}
    </>
  );
};
